/* eslint-disable no-underscore-dangle */
/* eslint-disable react/no-array-index-key */
import { RootState } from '@Common/models/redux/store';
import {
  Button,
  Grid,
  IconButton,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useRouter } from 'next/router';
import { safeJSONParse } from 'core/utils';
import QuillLocalStorage from 'core/utils/localStorageClient';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generateCitations } from 'src/researcher/handlers/api';
import {
  replaceTempLoadersWithCitedData,
  setupTempLoadersForCitations,
} from 'src/researcher/utils';
import { tab_height, tab_padding } from 'src/researcher/styles/constants';
import { fontWeight, UNITS, fontSize, unitPX } from 'src/researcher/styles';
import {
  APP_BAR_HEIGHT,
  LAYOUT_VALS,
  TinyToolbarContainerProps,
} from 'src/researcher/constants/common.constants';
import { ArrowBack } from '@mui/icons-material';
import { useCitationGeneratorDispatch, useCitationGeneratorState } from '../context';
import { setSearchQuery, toggleInplaceCreateForm } from '../context/actionCreators';
import {
  convertCitedDataResponse,
  getCitationStyleFromLocalStorage,
  scrollIntoViewCitationCard,
  getCgBreakpointDown,
} from '../utils';
import { CitationData } from '../types';
import {
  changeCitationStyle,
  addCitationsToList,
  setCitedData,
  rebuildCitationNumbers,
  addClonedCitedData,
} from '../reducers/actionCreators';
import ToolBarAndResultContainer from './TopBar';
import ExportCitationsBar from './ExportCitationsBar';
import CitationSelect from './CitationSelect';
import { CitationGeneratorXPath } from '../constants/xpath.constants';
import { CITATION_SELECT_HEIGHT } from '../constants/common.constants';
import CitationCard from './CitationCard';

const useStyles = makeStyles<Theme, { isCowriter: boolean }>((theme) => ({
  deleteIcon: {
    cursor: 'pointer',
    height: 31,
    width: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #C2C2C2',
    borderRadius: '3px',
  },
  createCitationButton: {
    fontSize: 12,
    fontWeight: 500,
    marginLeft: 10,
    height: '100%',
  },
  bottomBar: {
    height: '48px',
    marginTop: '2px',
    width: 'calc(100% - 1px)',
    backgroundColor: theme.palette.background.paper,
  },
  paperWidthSm: {
    maxWidth: '560px',
    maxHeight: '400px',
    height: '400px',
    width: '560px',
    padding: '0px 20px',
  },
  inTextPaperWidthSm: {
    maxWidth: '560px',
    maxHeight: '350px',
    height: '350px',
    width: '560px',
    padding: '0px 20px',
  },
  checkBoxLabel: {
    [theme.breakpoints.down(360)]: {
      fontSize: '12px',
    },
  },
  checked: {
    color: theme.specialColors.BLUE_COLOR,
    '&:hover': {
      color: theme.specialColors.BLUE_COLOR,
    },
  },

  checkBoxIcon: {
    padding: '6px ',
    [theme.breakpoints.down(360)]: {
      paddingRight: 0,
    },
    '&:hover': {
      color: theme.specialColors.BLUE_COLOR,
    },
  },
  checkBoxPrimary: {
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  labelRoot: {
    marginLeft: 5,
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
    },
    [theme.breakpoints.down(360)]: {
      marginRight: 0,
    },
  },
  defaultText: {
    marginTop: '-20px',
    fontSize: '16px',
    color: theme.palette.text.secondary,
  },
  rightBorder: {
    height: (props) => (props.isCowriter ? undefined : 'calc(100vh - 140px)'),
    position: 'relative',
    flexGrow: (props) => (props.isCowriter ? 1 : undefined),
  },
  pane: {
    padding: '0px 4px 0px 16px',
  },
  topHeading: {
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  topBar: {
    height: '48px',
  },
}));

const useCitationResultHeaderStyle = makeStyles((theme) => ({
  topHeading: {
    fontWeight: 'bold',
    // fontsize: '14px',
    // marginBottom: '10px',
  },
  iconButtonContainer: {
    paddingRight: ({ pathname }: { pathname: string }) =>
      pathname.includes('/co-writer') ? 20 : 15,
  },
}));

const CitationResultHeader = () => {
  const router = useRouter();
  const isCowriter = router.pathname.includes('/co-writer');
  const classes = useCitationResultHeaderStyle(router);
  const citationGeneratorDispatch = useCitationGeneratorDispatch();
  return (
    <Grid
      container
      justifyContent="space-between"
      alignItems="center"
      style={{ zIndex: 0 }}
      sx={[isCowriter && { pt: unitPX(UNITS['2pt']) }]}
    >
      <Grid item>
        {isCowriter ? (
          <Button
            variant="text"
            color="primary"
            size="small"
            startIcon={<ArrowBack />}
            onClick={() => citationGeneratorDispatch(toggleInplaceCreateForm(false))}
          >
            Back
          </Button>
        ) : (
          <Typography component="h2" className={classes.topHeading}>
            Create Citation
          </Typography>
        )}
      </Grid>
      {!isCowriter && (
        <Grid item className={classes.iconButtonContainer}>
          <IconButton
            onClick={() => citationGeneratorDispatch(toggleInplaceCreateForm(false))}
            size="large"
          >
            <CloseIcon
              style={{
                zIndex: 1,
                position: 'absolute',
              }}
            />
          </IconButton>
        </Grid>
      )}
    </Grid>
  );
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const anchor_height = UNITS['5pt'];

const getCitationListContainerHeight = (
  pathname: string,
  tool?: boolean,
  isSmallScreen?: boolean
) => {
  let height;
  if (isSmallScreen) {
    height = 'calc(100vh - 197px)';
  } else if (tool) {
    height = 'calc(100vh - 173px)';
  } else if (pathname.includes('/co-writer')) {
    height = `calc(${tab_height} - ${LAYOUT_VALS.drawerMenubarHeight} - ${unitPX(
      UNITS['3pt']
    )} - ${unitPX(UNITS['6pt'])} - ${unitPX(tab_padding)} - ${unitPX(anchor_height)})`;
  } else {
    height = 'calc(50vh - 17px)';
  }
  return height;
};

interface CitationsToShow extends CitationData {
  index?: number;
}
export default function CitationListContainer({
  cowriterToolbarAction = false,
}: {
  cowriterToolbarAction: boolean;
}) {
  const isSmallScreen = useMediaQuery<Theme>((theme) => getCgBreakpointDown(theme));
  const router = useRouter();
  const isCowriter = router.pathname.includes('/co-writer');
  const classes = useStyles({ isCowriter });

  const citationList = useSelector((state: RootState) => state.ctgr.citationList);
  const citationsFromCowriter = useSelector((state: RootState) => state.ctgr.citationsFromCowriter);
  const citedDataById = useSelector((state: RootState) => state.ctgr.citedDataById);
  const independentTool = useSelector((state: RootState) => state.common.independentTool);
  const citations = isCowriter ? citationsFromCowriter : citationList;
  const citationGeneratorDispatch = useCitationGeneratorDispatch();
  const query = useCitationGeneratorState((state) => state.searchQuery);
  const dispatch = useDispatch();
  const [citationsToShow, setCitationsToShow] = useState([] as CitationsToShow[]);
  const isCreateFormVisible = useCitationGeneratorState(
    (state) => state.isInplaceCreateFormOpenedFromCowriter
  );

  useEffect(() => {
    citationGeneratorDispatch(setSearchQuery(''));
    const citationStyle = getCitationStyleFromLocalStorage();
    dispatch(changeCitationStyle(citationStyle));

    // get citations from LS only in case of /citation-generator product
    if (!isCowriter) {
      const LocalStorage = QuillLocalStorage.getInstance();
      const citationListFromLS = safeJSONParse(LocalStorage.get('citationList', '[]'), []);
      if (citationListFromLS.length) {
        generateCitations({ style: citationStyle, citations: citationListFromLS }).then(
          (response) => {
            if (response.style !== citationStyle) {
              // silently change citationStyle
              dispatch(changeCitationStyle(response.style));
            }
            const citedData = convertCitedDataResponse(response);
            dispatch(setCitedData(citedData));
            dispatch(addCitationsToList(citationListFromLS));
            dispatch(rebuildCitationNumbers());
          }
        );
      }
    }
  }, []);

  useEffect(() => {
    const filteredCitations: CitationsToShow[] = [];
    if (query) {
      citations?.forEach((citation, index) => {
        const { contributors, publicationInfo } = citation;
        const { title, publisher } = publicationInfo;
        const lCquery = query.toLowerCase();
        if (
          title?.toLowerCase().includes(lCquery) ||
          publisher?.toLowerCase().includes(lCquery) ||
          contributors.toString().toLowerCase().includes(lCquery)
        ) {
          filteredCitations.push({ ...citation, index });
        }
      });
    } else {
      filteredCitations.push(...citations);
    }
    setCitationsToShow(filteredCitations);
  }, [citationList, citationsFromCowriter, query]);

  const postCloneCb = (oldId: string, newId: string) => {
    dispatch(addClonedCitedData(oldId, newId, citations.length + 1));
    scrollIntoViewCitationCard(newId, 20);
  };

  const postDeleteCb = () => {
    dispatch(rebuildCitationNumbers());

    if (isCowriter) {
      setupTempLoadersForCitations(citationsFromCowriter);
      setTimeout(replaceTempLoadersWithCitedData, 150, citationsFromCowriter);
    }
  };

  const hasCitations = citations.length > 0;

  // console.log({ hasCitations });

  return (
    <>
      {
        // this section is for co-writer
        isCreateFormVisible ? (
          <Grid container className={`${classes.rightBorder} ${classes.pane}`}>
            <Grid container className={classes.topBar}>
              <CitationResultHeader />
            </Grid>

            <ToolBarAndResultContainer />
          </Grid>
        ) : (
          <>
            <Grid
              container
              style={{
                padding: `${isCowriter ? unitPX(UNITS['3pt']) : unitPX(1)} ${
                  isCowriter ? unitPX(tab_padding) : unitPX(4)
                } 0px ${isCowriter ? unitPX(tab_padding) : unitPX(16)}`,
              }}
              sx={[isCowriter && { flexGrow: 1, display: 'flex' }]}
            >
              <Grid
                container
                item={isCowriter}
                sx={[isCowriter && { flexGrow: 1, justifyContent: 'flex-start' }]}
              >
                <Grid item sx={[{ width: '100%' }, isCowriter && { height: unitPX(UNITS['6pt']) }]}>
                  <Grid
                    container
                    sx={[
                      !isCowriter && {
                        paddingRight: unitPX(UNITS['1.5pt']),
                        marginTop: unitPX(-2),
                      },
                    ]}
                  >
                    <CitationSelect />

                    {isSmallScreen && citations.length > 0 && !isCowriter && (
                      <ExportCitationsBar isCompact citations={citations} />
                    )}

                    {router.pathname === '/citation-generator' ? (
                      ''
                    ) : (
                      <>
                        {hasCitations && (
                          <Tooltip arrow title="Create Citation">
                            <Button
                              {...CitationGeneratorXPath.ComposeCitation}
                              className={classes.createCitationButton}
                              sx={[
                                isCowriter && {
                                  borderRadius: unitPX(UNITS['2.5pt']),

                                  // height: `${unitPX(UNITS['5pt'])} !important`,
                                  fontSize: '14px !important',
                                  fontWeight: '600 !important',
                                },
                              ]}
                              startIcon={<AddIcon />}
                              color="primary"
                              variant={isCowriter ? 'contained' : 'outlined'}
                              // style={{ marginRight: 10 }}
                              onClick={() => {
                                citationGeneratorDispatch(toggleInplaceCreateForm(true));
                                // researcherTrack.citationCardActions('add_citation');
                              }}
                            >
                              {isCowriter ? 'Create Citation' : 'Citation'}
                            </Button>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Grid>
                </Grid>

                <Grid
                  sx={[
                    isCowriter && {
                      flexGrow: 1,
                      pb: unitPX(tab_padding),
                      // maxHeight: getCitationListContainerHeight(
                      //   router.pathname,
                      //   independentTool,
                      //   isSmallScreen
                      // ),
                    },
                  ]}
                  container
                  item
                  style={{
                    marginTop: isCowriter ? '0px' : '11px',
                    maxHeight: getCitationListContainerHeight(
                      router.pathname,
                      independentTool,
                      isSmallScreen
                    ),
                    height: getCitationListContainerHeight(
                      router.pathname,
                      independentTool,
                      isSmallScreen
                    ),
                    overflow: 'auto',
                    paddingRight: '8px',
                    display: 'block',
                  }}
                >
                  {citations.length > 0 ? (
                    <div id="citationCardsContainerId">
                      {citationsToShow?.length > 0 ? (
                        citationsToShow.map((citation, index) => (
                          <CitationCard
                            isLastItem={citationsToShow.length - 1 === index}
                            citation={citation}
                            key={citation.id || citation.refId}
                            index={citation.index || index}
                            cowriterToolbarAction={cowriterToolbarAction}
                            fullCitation={citedDataById[citation.refId]?.full || [{ text: '...' }]}
                            shortCitation={
                              citedDataById[citation.refId]?.short || [{ text: '...' }]
                            }
                            citationNumber={citedDataById[citation.refId]?.citationNumber}
                            onDelete={postDeleteCb}
                            onClone={postCloneCb}
                          />
                        ))
                      ) : (
                        <Typography>No result found</Typography>
                      )}
                    </div>
                  ) : (
                    <Grid
                      container
                      item
                      justifyContent="center"
                      alignItems="center"
                      style={{
                        height: '100%',
                      }}
                      sx={[
                        isCowriter && {
                          flexGrow: 1,
                          pb: unitPX(UNITS['20pt']),
                          flexDirection: 'column',
                        },
                      ]}
                    >
                      {isCowriter && !hasCitations && (
                        <>
                          <Typography className={classes.defaultText}>
                            Start a new citation or upload your existing projects.{' '}
                          </Typography>
                          <Tooltip arrow title="Create Citation">
                            <Button
                              {...CitationGeneratorXPath.ComposeCitation}
                              className={classes.createCitationButton}
                              sx={[
                                {
                                  borderRadius: unitPX(UNITS['2.5pt']),
                                  height: 'unset !important',
                                  mt: unitPX(UNITS['3pt']),
                                  fontSize: `${unitPX(14)} !important`,
                                  fontWeight: '600 !important',
                                },
                              ]}
                              startIcon={<AddIcon />}
                              color="primary"
                              variant={isCowriter ? 'contained' : 'outlined'}
                              onClick={() => {
                                citationGeneratorDispatch(toggleInplaceCreateForm(true));
                                // researcherTrack.citationCardActions('add_citation');
                              }}
                            >
                              Create Citation
                            </Button>
                          </Tooltip>
                        </>
                      )}
                      {!isCowriter && (
                        <Typography className={classes.defaultText}>
                          No citation has been added yet.
                        </Typography>
                      )}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>

            {!isSmallScreen && citations.length > 0 && <ExportCitationsBar citations={citations} />}
          </>
        )
      }
    </>
  );
}
